import {CaretDownOutlined, CaretUpOutlined, CheckCircleOutlined, LeftOutlined, RightOutlined} from '@ant-design/icons';
import {Button, Collapse, Divider, Steps} from 'antd';
import {jwtDecode} from 'jwt-decode';
import {useRef, useState} from 'react';
import {toast} from 'react-hot-toast';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';

import {RQCreateRazorpayOrder, RQGetUserDetails, RQOrderRegistration, RQVerifyRazorpayPayment, RqUpdateOrder} from '../../reactQuery/apis';
import {removeFromCart} from '../../redux/authAction';
import {selectAuth} from '../../redux/selectors';

import {AddressSection} from './AddressSection';
import {OrderSummary} from './OrderSummary';

const CheckOutScreen = () => {
  const location = useLocation();

  const navigate = useNavigate();
  
  const createdOrderRef = useRef(null);
  
  const productData = location.state?.product || JSON.parse(localStorage.getItem('checkoutProduct') || '[]');

  const isCart = productData?.items || (Array.isArray(productData) && productData[0]?.items);

  const storedProduct = isCart ? Array.isArray(productData) ? productData[0].items : productData.items : Array.isArray(productData) ? productData : [productData];

  const auth = useSelector(selectAuth);

  const token = auth?.tokenData;

  const {loggedIn} = useSelector(state => state.auth);

  const [isAddressComplete, setIsAddressComplete] = useState(false);

  const [hasUnsavedAddress, setHasUnsavedAddress] = useState(false);

  const [deliveryCharges, setdeliveryCharges] = useState(0);

  const [deliveryDays, setDeliveryDays] = useState(null);

  const [activeKey, setActiveKey] = useState(['1']);

  const [currentPanel, setCurrentPanel] = useState(1);

  const [loading, setLoading] = useState(false);

  const decodedToken = token ? jwtDecode(token) : null;

  const id = decodedToken?.userId;

  const {data: userData} = RQGetUserDetails(id, token);

  const {mutateAsync} = RQOrderRegistration(token);

  const dispatch = useDispatch();

  const handleLogin = () => {
    if (storedProduct) {
      localStorage.setItem('checkoutProduct', JSON.stringify(Array.isArray(productData) ? productData : [productData]));
    }
    navigate('/auth/login', {state: {from: '/checkout'}});
  };
  const {mutate: createRazorpayOrder} = RQCreateRazorpayOrder({
    onSuccess: rzpResponse => {
      openRazorpay(rzpResponse);
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'Something Went Wrong');
    },
  });
  const {mutate: verifyPayment} = RQVerifyRazorpayPayment({

    onSuccess: async (data, variables) => {

      const transactionId = variables.formData.transactionId;

      const finalOrder = {

        ...orderDataRef.current,

        transactionId: transactionId,

        estimatedDeliveryDate: new Date(
          Date.now() + 5 * 24 * 60 * 60 * 1000,
        ).toISOString(),

      };

      const orderResponse = await mutateAsync(finalOrder);

      toast.success('Order placed successfully!');

      storedProduct.forEach(item =>
        dispatch(removeFromCart({
          productId: item.productId,
          size: item.size,
        })),
      );

      localStorage.removeItem('checkoutProduct');

      navigate('/');

    },

    onError: () => {
      toast.error('Payment verification failed');
    },

  });
  const {mutate: updateOrder} = RqUpdateOrder({
    onSuccess: () => {
      toast.success('Order updated successfully!');

      localStorage.removeItem('checkoutProduct');

      storedProduct.forEach(item =>
        dispatch(removeFromCart({productId: item.productId, size: item.size})),
      );

      navigate('/', {replace: true});
    },

    onError: error => {
      toast.error(error?.response?.data?.message || 'Order update failed');
    },
  });
  const subTotal = storedProduct.reduce((sum, item) => sum + item.pricePerUnit * item.quantity, 0);
  const totalAmount = subTotal+deliveryCharges;
  const totalQuantity = storedProduct.length;
  const openRazorpay = rzpData => {
    const options = {
      key: rzpData.key,
      order_id: rzpData.razorpayOrderId,
      amount: rzpData.amount,
      currency: rzpData.currency,
      name: 'RatnaParkhi Jewellers',
      description: 'Jewelry Purchase Payment',

      handler: response => {

        verifyPayment({
          token,
          formData: {
            userId: userData.id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            transactionId: response.razorpay_payment_id,
          },
        });

      },

      modal: {
        ondismiss: function () {
          toast.error('Payment cancelled');
        },
      },

      prefill: rzpData.prefill,
      theme: {color: '#000'},
    };

    const razorpayObject = new window.Razorpay(options);
    razorpayObject.open();
  };
  const handlePlaceOrder = async () => {

    if (loading) {return;}

    if (!loggedIn) {
      toast.error('Please login first');
      handleLogin();
      return;
    }

    if (!isAddressComplete) {
      toast.error('Please complete address');
      return;
    }

    if (totalAmount < 1) {
      toast.error('Amount must be at least ₹1');
      return;
    }

    setLoading(true);

    try {
      prepareOrderData(); 

      const res = await loadRazorpayScript();

      if (!res) {
        toast.error('Razorpay SDK failed');
        return;
      }

      createRazorpayOrder({
        token,
        amount: Number(totalAmount),
      });
    } finally {
      setLoading(false);
    }
    console.log('Order Data Prepared:', orderDataRef.current);
    console.log('totalAmount:', totalAmount);
  };
  const orderDataRef = useRef(null);

  const prepareOrderData = () => {

    const orderData = {

      userId: userData.id,

      products: storedProduct.map(item => ({
        productId: item.productId,
        productName: item.productName,
        productType: item.productType,
        jewelryType: item.jewelryType || 'GOLD',

        size: item.size ? [
          {
            size: item.size,
            quantity: item.quantity.toString(),
          },
        ] : [],

        quantity: Number(item.quantity),
        pricePerUnit: Number(item.pricePerUnit),

        weight: item.weight?.netWeight || '',

        gift: item.gift || false,
      })),

      status: 'CONFIRMED',

      totalAmount: Number(totalAmount),

      totalQuantity: Number(totalQuantity),

      deliveryCharges: Number(deliveryCharges),

      transactionStatus: 'FULL_PAYMENT',

    };

    orderDataRef.current = orderData;

  };
  const steps = [
    {title: 'Login', status: loggedIn ? 'finish' : 'process'},
    {title: 'Address', status: isAddressComplete ? 'finish' : loggedIn ? 'process' : 'wait'},
    {title: 'Order', status: 'wait'},
  ];

  const handleNext = () => {
    if (currentPanel === 1 && !isAddressComplete) {
      toast.error('Please complete your address details.');
      return;
    }
    if (currentPanel < 2) {
      setCurrentPanel(currentPanel + 1);
      setActiveKey([(currentPanel + 1).toString()]);
    }
    if (hasUnsavedAddress) {
      toast.error('Please save your address before proceeding!');
      return;
    }
  };

  const handlePrev = () => {
    if (currentPanel > 1) {
      setCurrentPanel(currentPanel - 1);
      setActiveKey([(currentPanel - 1).toString()]);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise(resolve => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  return (
    <div className={'flex w-full items-center justify-center lg:px-20 p-4 lg:mb-20'}>
      <div className={'max-w-7xl w-full'}>
        <div className={'grid grid-cols-1 lg:grid-cols-12 lg:gap-5 w-full max-w-screen-lg mx-auto p-4 lg:p-0'}>
          <div className={'lg:col-span-12 mt-5 lg:block hidden'}>
            <Steps current={steps.findIndex(step => step.status === 'process')} items={steps} />
          </div>
          <div className={'lg:col-span-7 mt-5'}>
            <div className={'flex flex-col gap-4'}>
              <Collapse
                className={'w-full shadow-md'}
                bordered={false}
                defaultActiveKey={['1']}
                expandIcon={({isActive}) => (
                  <div className={'flex items-center gap-2'}>
                    {isActive ? <CaretUpOutlined /> : <CaretDownOutlined />}
                  </div>
                )}
                expandIconPosition={'end'}
              >
                <Collapse.Panel
                  key={'1'}
                  header={(
                    <span className={'flex items-center font-semibold text-primary-900 w-full lg:text-base'}>
                    Login {loggedIn && <CheckCircleOutlined className={'ml-2'} />}
                    </span>
                  )}
                >
                  <div className={'flex flex-col gap-2'}>
                    {loggedIn ? (
                      <p className={'text-primary-800 font-semibold'}>{userData?.firstName} {userData?.lastName}</p>
                    ) : (
                      <Button onClick={handleLogin} className={'!bg-primary-800 !text-white w-24'}>
                      Login
                      </Button>
                    )}
                  </div>
                </Collapse.Panel>
              </Collapse>

              {loggedIn && (
                <Collapse
                  className={'w-full shadow-md'}
                  bordered={false}
                  expandIcon={({isActive}) => (
                    <div className={'flex items-center gap-2'}>
                      {isActive ? <CaretUpOutlined /> : <CaretDownOutlined />}
                    </div>
                  )}
                  expandIconPosition={'end'}
                  activeKey={activeKey}
                >
                  <Collapse.Panel
                    header={(
                      <span className={'flex items-center font-semibold text-primary-900 w-full lg:text-base'}>
                      Address {isAddressComplete && <CheckCircleOutlined className={'ml-2 text-primary-900'} />}
                      </span>
                    )}
                    key={'1'}
                  >
                    <AddressSection 
                      setAddressComplete={setIsAddressComplete}
                      setdeliveryCharges={setdeliveryCharges}
                      setDeliveryDays={setDeliveryDays}
                      setHasUnsavedAddress={setHasUnsavedAddress}
                    />                    <div className={'w-full flex justify-end'}>
                      {currentPanel > 1 && (
                        <Button onClick={handlePrev} className={'flex items-center justify-center gap-1 text-xs font-bold border border-primary-800 text-primary-800 rounded-full lg:px-3 px-2 py-1 hover:bg-primary-100 transition duration-200 shadow-sm'}
                        >
                        Prev
                        </Button>
                      )}
                      <Button
                        onClick={handleNext}
                        className={'flex items-center justify-center gap-1 text-xs font-semibold border border-primary-800 bg-primary-900 text-white rounded-full lg:px-3 px-2 py-1 hover:bg-primary-100 transition duration-200 shadow-sm'}
                      >
                      Next <RightOutlined />
                      </Button>
                    </div>
                  </Collapse.Panel>

                  <Collapse.Panel
                    header={<span className={'flex items-center font-semibold text-primary-900 w-full lg:text-base'}>Order Summary</span>}
                    key={'2'}
                  >
                    <OrderSummary product={storedProduct} />
                    <div className={'w-full flex justify-end'}>
                      <div className={'flex justify-between mt-4 gap-1'}>
                        <Button
                          onClick={handlePrev}
                          className={'flex items-center justify-center gap-1 text-xs font-bold border border-primary-800 text-primary-800 rounded-full lg:px-3 px-2 py-1 hover:bg-primary-100 transition duration-200 shadow-sm'}
                        >
                          <LeftOutlined /> Prev
                        </Button>
                      </div>
                    </div>
                  </Collapse.Panel>           
                </Collapse>
              )}
            </div>
          </div>

          <div className={'lg:col-span-5 lg:ml-4 mt-5 lg:sticky top-0 self-start'}>
            <div className={'bg-primary-50 rounded-lg p-4 w-full'}>
              <h1 className={'text-primary-900 font-semibold text-lg'}>
    Price Details
              </h1>

              <Divider className={'my-2'} />

              <div className={'flex justify-between text-sm font-medium mb-2'}>
                <p>Price ({totalQuantity} items)</p>
                <p>₹ {subTotal}</p>
              </div>

              <div className={'flex justify-between text-sm font-medium mb-2'}>
                <p>Delivery charges</p>
                <p>
                  {deliveryCharges === 0
                    ? 'Free'
                    : `₹ ${deliveryCharges}`}
                </p>
              </div>

              <Divider className={'my-2'} />

              <div className={'flex justify-between text-base font-semibold'}>
                <p>Total Amount</p>
                <p>₹ {totalAmount}</p>
              </div>

              <p className={'text-sm text-gray-600 mt-2'}>
                {deliveryDays
                  ? `Estimated delivery in ${deliveryDays} day${deliveryDays > 1 ? 's' : ''}`
                  : 'Select state to see delivery time'}
              </p>
            </div>

            <p className={'text-sm font-semibold text-gray-600 mt-2'}>
Note: Order can be cancelled anytime before it is shipped.
            </p>
            {loggedIn && (
              <Button 
                onClick={handlePlaceOrder}
                disabled={loading || !isAddressComplete || currentPanel !== 2 || hasUnsavedAddress}
                className={'mt-5 group w-full h-8 rounded-lg border-none bg-primary-900 flex items-center justify-center cursor-pointer transition duration-500 overflow-hidden shadow-md relative active:scale-95 text-sm hover:shadow-[0_0_5px_#3D180F]'}>

                <span className={'h-full w-fit flex items-center justify-center text-white z-[1] transition duration-500 text-[1.04em] font-semibold group-hover:text-[#3D180F]'}>
                  {loading ? 'Processing...' : 'Place Order'}
                </span>
              </Button>
            )}
            {loggedIn && !isAddressComplete && (
              <p className={'text-[#FF0000] text-sm mt-1'}>
              Please complete your address details.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckOutScreen;
